export const RELOAD_GUARD_KEY = 'chunkLoadErrorReload';
export const RELOAD_GUARD_TTL_MS = 10000;

// Set once this handler's listeners are actually attached. RollbarScript.js's checkIgnore reads
// this before treating a chunk failure as self-healing: its script runs from _document, before
// _app.js (and this module) executes, so a failure that early can't have been caught here — it
// would otherwise be hidden with no reload ever happening.
export const HANDLER_READY_FLAG = '__workableChunkReloadReady';

// The concrete, stale-CDN-asset signature — also read by RollbarScript.js's checkIgnore so the
// two never drift apart. Deliberately narrower than isChunkLoadError below: a bare "ChunkLoadError"
// can also mean a genuine, non-self-healing failure (e.g. a network timeout), so it's only used
// here to decide whether *this handler* should attempt a reload, not whether Rollbar should ignore it.
export const CHUNK_LOAD_ERROR_PATTERN = /Loading (CSS )?chunk [\w-]+ failed/i;

const isChunkLoadError = (message) =>
  typeof message === 'string' &&
  (CHUNK_LOAD_ERROR_PATTERN.test(message) || /ChunkLoadError/i.test(message));

const isGuardExpired = (setAt) => {
  const age = Date.now() - Number(setAt);
  // A malformed/corrupted persisted value yields NaN, which is never >= the TTL — treat it
  // as expired too, otherwise a bad value could suppress recovery for the tab indefinitely.
  return !Number.isFinite(age) || age >= RELOAD_GUARD_TTL_MS;
};

const reloadOnce = () => {
  try {
    // The guard is only ever cleared by expiring (see setupChunkErrorReload below) — treating
    // an expired value as clear here is what lets a tab retry after the TTL, whether the
    // previous reload reached a fresh page or not.
    const existing = sessionStorage.getItem(RELOAD_GUARD_KEY);
    if (existing && !isGuardExpired(existing)) return;
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  } catch (e) {
    // Can't persist the guard, so we can't tell a fresh failure from a reload loop — skip reloading.
    return;
  }
  window.location.reload();
};

// A tab left open across a deploy still references the previous build's chunk
// filenames (content-hashed), which the CDN no longer serves once a new build
// replaces them. Reloading once picks up the new build's asset manifest.
//
// The guard is deliberately left in place until it expires (RELOAD_GUARD_TTL_MS) rather than
// being cleared on the next successful 'load': a persistent, non-stale-asset failure (e.g. a
// chunk that's genuinely broken, not just stale) would otherwise reach 'load', have its guard
// wiped immediately, fail again, and reload again — an unthrottled loop. Waiting out the TTL
// caps that to at most one reload per window, at the cost of a later deploy's retry not being
// instant — an acceptable trade since it's still just one TTL window's delay.
const setupChunkErrorReload = () => {
  window.addEventListener('error', (event) => {
    if (isChunkLoadError(event?.message) || isChunkLoadError(event?.error?.message)) {
      reloadOnce();
    }
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event?.reason;
    const message = typeof reason === 'string' ? reason : reason?.message;
    if (isChunkLoadError(message) || reason?.name === 'ChunkLoadError') {
      reloadOnce();
    }
  });

  window[HANDLER_READY_FLAG] = true;
};

export default setupChunkErrorReload;
